import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { AttachmentTemplate, AttachmentSelection } from '../services/AttachmentService';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

interface AttachmentsPanelProps {
  attachments: AttachmentTemplate[];
  selections: AttachmentSelection[];
  onSelectionsChange: (selections: AttachmentSelection[]) => void;
  validationErrors?: Record<string, string>;
}

export function AttachmentsPanel({
  attachments,
  selections,
  onSelectionsChange,
  validationErrors = {},
}: AttachmentsPanelProps) {
  const [expandedDetails, setExpandedDetails] = useState<Set<number>>(new Set());

  // Separate by category, with fallbacks
  let recommended = attachments.filter(a => a.category === 'recommended');
  let optional = attachments.filter(a => a.category === 'optional');

  // Fallback: If both are empty but we have attachments, categorize by default_selected
  if (recommended.length === 0 && optional.length === 0 && attachments.length > 0) {
    recommended = attachments.filter(a => a.default_selected === true);
    optional = attachments.filter(a => a.default_selected !== true);
  }

  const isSelected = (templateId: number): boolean => {
    return selections.some(s => s.template_id === templateId);
  };

  const getSelection = (templateId: number): AttachmentSelection | undefined => {
    return selections.find(s => s.template_id === templateId);
  };

  const toggleSelection = (attachment: AttachmentTemplate) => {
    const selected = isSelected(attachment.template_id);

    if (selected) {
      onSelectionsChange(selections.filter(s => s.template_id !== attachment.template_id));
    } else {
      const newSelection: AttachmentSelection = {
        template_id: attachment.template_id,
        order_index: attachment.order_index || selections.length + 1,
        variables: {},
      };
      onSelectionsChange([...selections, newSelection]);
    }
  };

  const updateVariable = (templateId: number, key: string, value: any) => {
    const updatedSelections = selections.map(s => {
      if (s.template_id === templateId) {
        return {
          ...s,
          variables: {
            ...s.variables,
            [key]: value,
          },
        };
      }
      return s;
    });
    onSelectionsChange(updatedSelections);
  };

  const toggleDetails = (templateId: number) => {
    const newExpanded = new Set(expandedDetails);
    if (newExpanded.has(templateId)) {
      newExpanded.delete(templateId);
    } else {
      newExpanded.add(templateId);
    }
    setExpandedDetails(newExpanded);
  };

  const renderAttachmentRow = (attachment: AttachmentTemplate) => {
    const selected = isSelected(attachment.template_id);
    const selection = getSelection(attachment.template_id);
    const hasVariables = attachment.variables_schema && attachment.variables_schema.length > 0;
    const showDetails = expandedDetails.has(attachment.template_id);

    return (
      <div key={attachment.template_id} style={{ marginBottom: 8 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            backgroundColor: selected ? colors.infoLight : colors.bgCard,
            cursor: 'pointer',
          }}
          onClick={() => !hasVariables && toggleSelection(attachment)}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => toggleSelection(attachment)}
            onClick={(e) => e.stopPropagation()}
            style={{ marginRight: 10, cursor: 'pointer' }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: colors.navy }}>
                {attachment.template_name}
              </div>
              {attachment.category === 'recommended' && (
                <span
                  style={{
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 600,
                    backgroundColor: colors.successLight,
                    color: colors.success,
                  }}
                  title="Empfohlen aufgrund Work Area / Position"
                >
                  EMPFOHLEN
                </span>
              )}
            </div>
            {attachment.description && (
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                {attachment.description}
              </div>
            )}
          </div>
          {hasVariables && selected && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleDetails(attachment.template_id);
              }}
              style={{
                padding: '4px 8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.info,
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              Details {showDetails ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            </button>
          )}
        </div>

        {hasVariables && selected && showDetails && (
          <div
            style={{
              marginTop: 8,
              marginLeft: 32,
              padding: 12,
              backgroundColor: colors.bgMuted,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
            }}
          >
            {attachment.variables_schema?.map(variable => {
              const variableKey = `${attachment.template_id}_${variable.key}`;
              const error = validationErrors[variableKey];
              const value = selection?.variables?.[variable.key] || '';

              return (
                <div key={variable.key} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500, color: colors.textMuted }}>
                    {variable.label}
                    {variable.required && <span style={{ color: colors.danger, marginLeft: 2 }}>*</span>}
                  </label>
                  {variable.type === 'select' ? (
                    <select
                      value={value}
                      onChange={(e) => updateVariable(attachment.template_id, variable.key, e.target.value)}
                      style={{
                        ...ps.inputStyle,
                        border: error ? `1px solid ${colors.danger}` : `1px solid ${colors.border}`,
                      }}
                    >
                      <option value="">— Auswählen —</option>
                      {variable.options?.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : variable.type === 'date' ? (
                    <input
                      type="date"
                      value={value}
                      onChange={(e) => updateVariable(attachment.template_id, variable.key, e.target.value)}
                      style={{
                        ...ps.inputStyle,
                        border: error ? `1px solid ${colors.danger}` : `1px solid ${colors.border}`,
                      }}
                    />
                  ) : variable.type === 'number' || variable.type === 'decimal' ? (
                    <input
                      type="number"
                      step={variable.type === 'decimal' ? '0.01' : '1'}
                      value={value}
                      onChange={(e) => updateVariable(attachment.template_id, variable.key, variable.type === 'decimal' ? parseFloat(e.target.value) : parseInt(e.target.value))}
                      style={{
                        ...ps.inputStyle,
                        border: error ? `1px solid ${colors.danger}` : `1px solid ${colors.border}`,
                      }}
                    />
                  ) : variable.type === 'checkbox' || variable.type === 'boolean' ? (
                    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0' }}>
                      <input
                        type="checkbox"
                        checked={value === true || value === 'true'}
                        onChange={(e) => updateVariable(attachment.template_id, variable.key, e.target.checked)}
                        style={{ marginRight: 8, cursor: 'pointer', width: 16, height: 16 }}
                      />
                      <span style={{ fontSize: 13, color: colors.textMuted }}>
                        {variable.help_text || variable.label}
                      </span>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateVariable(attachment.template_id, variable.key, e.target.value)}
                      style={{
                        ...ps.inputStyle,
                        border: error ? `1px solid ${colors.danger}` : `1px solid ${colors.border}`,
                      }}
                    />
                  )}
                  {variable.help_text && (
                    <div style={{ fontSize: 11, color: colors.textLight, marginTop: 2 }}>
                      {variable.help_text}
                    </div>
                  )}
                  {error && (
                    <div style={{ fontSize: 11, color: colors.danger, marginTop: 2 }}>
                      {error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        padding: 16,
        backgroundColor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        marginTop: 16,
      }}
    >
      <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600, color: colors.navy }}>
        Anhänge
      </h4>

      {recommended.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
            Empfohlen
          </div>
          {recommended.map(renderAttachmentRow)}
        </div>
      )}

      {optional.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: colors.textMuted, marginBottom: 8 }}>
            Weitere verfügbar (optional)
          </div>
          {optional.map(renderAttachmentRow)}
        </div>
      )}
    </div>
  );
}
